<template>
  <teleport v-if="mounted && targetExists" :to="targetSelector">
    <slot></slot>
  </teleport>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'

defineSlots<{
  default: () => unknown
}>()

// Configuración del componente
const targetSelector = ref('#header-buttons-container')
const maxRetries = 10
const retryInterval = 200

// Variables de estado
const targetExists = ref(false)
const mounted = ref(false)
const initialCheckComplete = ref(false)
let retryCount = 0
let checkInterval: number | null = null
let observer: MutationObserver | null = null
let debounceTimer: number | null = null

// Función debounce para evitar llamadas excesivas
const debouncedCheckTarget = () => {
  if (debounceTimer) {
    clearTimeout(debounceTimer)
  }
  debounceTimer = window.setTimeout(() => {
    checkTargetExists()
  }, 50)
}

// Verifica si el elemento destino existe en el DOM
const checkTargetExists = async () => {
  const target = document.querySelector(targetSelector.value)
  const exists = !!target

  // Evitar cambios innecesarios del estado reactivo
  if (exists && !targetExists.value) {
    targetExists.value = true

    if (checkInterval) {
      clearInterval(checkInterval)
      checkInterval = null
    }

    // Detener el observer una vez que encontramos el target
    if (observer) {
      observer.disconnect()
      observer = null
    }
  } else if (!exists && targetExists.value) {
    targetExists.value = false
    retryCount++

    if (retryCount > maxRetries && checkInterval) {
      clearInterval(checkInterval)
      checkInterval = null
    }
  }

  if (!initialCheckComplete.value) {
    initialCheckComplete.value = true
  }
}

// Configura el sistema de verificación del destino
const setupChecks = () => {
  // Limpiar recursos existentes antes de crear nuevos
  if (checkInterval) {
    clearInterval(checkInterval)
    checkInterval = null
  }

  if (observer) {
    observer.disconnect()
    observer = null
  }

  if (debounceTimer) {
    clearTimeout(debounceTimer)
    debounceTimer = null
  }

  checkTargetExists()

  // Solo crear el interval si el target aún no existe
  if (!targetExists.value) {
    checkInterval = window.setInterval(() => {
      checkTargetExists()
    }, retryInterval)

    observer = new MutationObserver((mutations) => {
      // Verificar si las mutaciones son relevantes para evitar llamadas innecesarias
      const hasRelevantChanges = mutations.some(mutation =>
        mutation.type === 'childList' &&
        (mutation.addedNodes.length > 0 || mutation.removedNodes.length > 0)
      )

      if (hasRelevantChanges && !targetExists.value) {
        debouncedCheckTarget()
      }
    })

    observer.observe(document.body, {
      childList: true,
      subtree: true
    })
  }
}

onMounted(() => {
  mounted.value = true
  setupChecks()

  if ('history' in window && 'pushState' in window.history) {
    window.addEventListener('popstate', () => {
      retryCount = 0
      if (!checkInterval) {
        setupChecks()
      }
    })
  }

  window.addEventListener('load', () => {
    checkTargetExists()
  })
})

onUnmounted(() => {
  if (observer) {
    observer.disconnect()
    observer = null
  }

  if (checkInterval) {
    clearInterval(checkInterval)
    checkInterval = null
  }

  if (debounceTimer) {
    clearTimeout(debounceTimer)
    debounceTimer = null
  }

  window.removeEventListener('popstate', () => {
    retryCount = 0
    if (!checkInterval) {
      setupChecks()
    }
  })

  window.removeEventListener('load', () => {
    checkTargetExists()
  })
})
</script>
